import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, Settings2 } from 'lucide-react';
import SearchBar from './SearchBar';
import ModelSelector from '../unified-search/ModelSelector';
import AgentSelector from '../unified-search/AgentSelector';
import DomainSelector from '../unified-search/DomainSelector';
import DeepResearchToggle from '../unified-search/DeepResearchToggle';
import { ReadDatabase } from '../../../api/OasisBackendApi';
import { toast, ToastContainer } from 'react-toastify';

interface UnifiedSearchBarProps {
  onSearch?: (query: string, params: { model: string; agent: string; agentParams: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean } }) => boolean | void;
  initialValue?: string;
  isLoading?: boolean;
  initialPlaceholderMessage?: string;
  autoFocus?: boolean;
  className?: string;
  initialModel?: string;
  initialAgent?: string;
  initialAgentParams?: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean };
  onModelChange?: (model: string) => void;
  onAgentChange?: (agent: string) => void;
  onAgentParamsChange?: (params: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean }) => void;
  domains?: ReadDatabase[] | null;
  conversationId?: number;
}

const UnifiedSearchBar: React.FC<UnifiedSearchBarProps> = ({
  onSearch,
  initialValue = '',
  isLoading = false,
  initialPlaceholderMessage = 'Ask anything or search...',
  autoFocus = false,
  className = '',
  initialModel = 'gpt-4.1-mini',
  initialAgent = 'search_engine',
  initialAgentParams = { domain_id: [], available_domains: [] },
  domains,
  onModelChange,
  onAgentChange,
  onAgentParamsChange,
  conversationId,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [placeholderMessage, setPlaceholderMessage] = useState(initialPlaceholderMessage);

  // Internal state for when parent doesn't control the state
  const [currentModel, setCurrentModel] = useState(initialModel);
  const [currentAgent, setCurrentAgent] = useState(initialAgent);
  const [agentParams, setAgentParams] = useState<{
    domain_id: string[];
    available_domains?: { id: string; name: string }[];
    deep_research?: boolean;
  }>(initialAgentParams);
  const [deepResearchEnabled, setDeepResearchEnabled] = useState(initialAgentParams?.deep_research || false);

  const [searchParams, setSearchParams] = useSearchParams();

  console.log("Current Agent", currentAgent);
  
  // Update agent params when initialAgentParams changes (e.g., from URL parameters)
  useEffect(() => {
    if (initialAgentParams) {
      setAgentParams(initialAgentParams);
      if (initialAgentParams.deep_research !== undefined) {
        setDeepResearchEnabled(initialAgentParams.deep_research);
      }
    }
  }, [initialAgentParams]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  

  const handleSearch = (searchQuery: string): boolean => {
    if (currentAgent === "multi_domain" && !agentParams.available_domains) {
      toast.error("Please select a domain");
      return false;
    }
   
    if (onSearch) {
      if (currentAgent === "multi_domain" && !agentParams.domain_id.length) {
        toast.error("Please select a domain");
        return false;
      }
      // Pass all parameters to the parent component
      const searchParams = {
        model: currentModel,
        agent: currentAgent,
        agentParams: {
          ...agentParams,
          domain_id: agentParams.domain_id,
          available_domains: agentParams.available_domains || [],
          deep_research: deepResearchEnabled
        },
      };

      onSearch(searchQuery, searchParams);
      setPlaceholderMessage("Ask your next question on this topic or anything else...")
      return true;
    } else {
      return true;
    }
  };

  const handleModelChange = (model: string) => {
    if (onModelChange) {
      onModelChange(model);
    } 
    setCurrentModel(model);
  };

  const handleAgentChange = async (agent: string) => {
    setCurrentAgent(agent);
    onAgentChange?.(agent);
    

    // Update URL with new agent
    const newParams = new URLSearchParams(searchParams);
    newParams.set('agent', agent);
    setSearchParams(newParams);
    if (agent === 'search_engine') {
      if (conversationId) {
        navigate(`/search/${conversationId}`);
      } else {
        navigate(`/search`);
      }
    } else if (agent === 'multi_domain') {
      if (conversationId) {
        navigate(`/chat/${conversationId}`);
      } else {
        navigate(`/chat`);
      }
    }
    
  };

  const handleDomainSelect = (domainId: string | { id: string; name: string }[]) => {
    // Handle both single string and array inputs from unified-search DomainSelector
    if (typeof domainId === 'string') {
      // Single domain selection
      const domain = domains?.find(d => d.id === domainId);
      const domainObject = domain ? { id: domain.id, name: domain.name } : { id: domainId, name: domainId };
      
      if (onAgentParamsChange) {
        onAgentParamsChange({
          domain_id: [domainId],
          available_domains: [domainObject],
          deep_research: deepResearchEnabled
        });
      }
      setAgentParams({
        domain_id: [domainId],
        available_domains: [domainObject],
        deep_research: deepResearchEnabled
      });
    } else {
      // Array of domain objects (multi-select)
      const domainIds = domainId.map(d => d.id);
      if (onAgentParamsChange) {
        onAgentParamsChange({
          domain_id: domainIds,
          available_domains: domainId,
          deep_research: deepResearchEnabled
        });
      }
      setAgentParams({
        domain_id: domainIds,
        available_domains: domainId,
        deep_research: deepResearchEnabled
      });
    }
  };

  const handleDeepResearchToggle = (enabled: boolean) => {
    setDeepResearchEnabled(enabled);
    if (onAgentParamsChange) {
      onAgentParamsChange({
        ...agentParams,
        deep_research: enabled
      });
    }
    setAgentParams(prev => ({
      ...prev,
      deep_research: enabled
    }));
  };

  const handleInputChange = (value: string) => {
    setQuery(value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setIsExpanded(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div 
      ref={containerRef} 
      className={`w-full max-w-4xl mx-auto ${className}`}
    >
      <ToastContainer />
      <div 
        className={`relative transition-all duration-200 rounded-xl border ${
          isFocused 
            ? 'border-[var(--rhea-accent-primary)] shadow-lg' 
            : 'border-[var(--rhea-card-border)]'
        }`}
        style={{
          backgroundColor: 'var(--rhea-bg-primary)',
        }}
      >
        <div className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                onSearch={handleSearch}
                initialValue={query}
                isLoading={isLoading}
                placeholderMessage={placeholderMessage}
                autoFocus={autoFocus}
                className="w-full"
                onValueChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                deleteOnSubmit={true}
              />
            </div>
          </div>

          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-[var(--rhea-card-border)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 sm:w-48">
                    <ModelSelector
                      currentModel={currentModel}
                      onModelChange={handleModelChange}
                    />
                  </div>
                  
                  <div className="w-12 sm:w-48">
                    <AgentSelector
                      currentAgent={currentAgent}
                      onAgentChange={handleAgentChange}
                    />
                  </div>
                  
                  {(currentAgent === 'multi_domain' || currentAgent === 'search_engine') ? (
                    <>
                      {domains ? (
                        <div className="w-48">
                          <DomainSelector
                            onDomainSelect={handleDomainSelect}
                            initialDomains={agentParams?.domain_id || []}
                            multiSelect={currentAgent === 'multi_domain' || currentAgent === 'search_engine'}
                            domains={domains}
                          />
                        </div>
                      ) : (
                        <div className="w-48 text-sm text-[var(--rhea-text-secondary)]">
                          Loading Domains...
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
                
                {currentAgent === 'search_engine' && (
                  <DeepResearchToggle
                    isEnabled={deepResearchEnabled}
                    onToggle={handleDeepResearchToggle}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedSearchBar; 
